import {
  domainStatusSchema,
  provisioningStepSchema,
  type ServiceCheck,
  type ServiceFacts,
  type ServiceTicket,
  type ServiceTicketSeverity,
  type ServiceTicketStatus,
} from '@avrahami/contracts';
import { formatDate, type BadgeTone } from '@avrahami/ui';
import { he } from '../i18n/he';

/**
 * How a ticket reads on the platform's screens (adr/0036): a scan finding is named by its check
 * and explained from the facts the scan saw; a store's call by its own subject.
 */

type Described = Pick<ServiceTicket, 'source' | 'check' | 'subject' | 'facts'>;

export function ticketTitle(ticket: Described): string {
  if (ticket.check !== null) return he.service.checks[ticket.check];
  return ticket.subject ?? '';
}

const text = (facts: ServiceFacts, key: string) => {
  const value = facts[key];
  return value === undefined ? '' : String(value);
};
const number = (facts: ServiceFacts, key: string) => {
  const value = facts[key];
  return typeof value === 'number' ? value : Number(value ?? 0);
};
/** A day the scan wrote as YYYY-MM-DD, as the screens show dates. */
const day = (facts: ServiceFacts, key: string) => {
  const value = text(facts, key);
  return value === '' ? '' : formatDate(`${value}T12:00:00Z`);
};

/** What the scan saw, in words; null for a store's call (its words are in the messages). */
export function ticketExplanation(ticket: Described): string | null {
  const check: ServiceCheck | null = ticket.check;
  if (check === null) return null;
  const facts = ticket.facts;
  const explain = he.service.explain;
  switch (check) {
    case 'ACCOUNT_LOCKED':
      return explain.ACCOUNT_LOCKED(number(facts, 'count'));
    case 'FAILED_SIGN_INS':
      return explain.FAILED_SIGN_INS(number(facts, 'count'), number(facts, 'attempts'));
    case 'CERTIFICATE_EXPIRING':
      return explain.CERTIFICATE_EXPIRING(text(facts, 'hostname'), day(facts, 'until'));
    case 'OWNER_WITHOUT_PASSWORD':
      return explain.OWNER_WITHOUT_PASSWORD;
    case 'DOMAIN_PROBLEM': {
      const status = domainStatusSchema.safeParse(facts['status']);
      return explain.DOMAIN_PROBLEM(
        text(facts, 'hostname'),
        status.success ? he.domains.status[status.data] : text(facts, 'status'),
      );
    }
    case 'MAIL_FAILED':
      return explain.MAIL_FAILED(day(facts, 'since'));
    case 'NO_CONTACT':
      return explain.NO_CONTACT;
    case 'SETUP_FAILED': {
      const step = provisioningStepSchema.safeParse(facts['step']);
      return explain.SETUP_FAILED(
        step.success ? he.provisioning.steps[step.data] : text(facts, 'step'),
      );
    }
    case 'COPY_FAILED':
      return explain.COPY_FAILED(number(facts, 'count'));
  }
}

/** The store's screen where a scan finding is dealt with. */
export type FixScreen = 'domains' | 'contact' | 'copy' | 'store';

export function fixScreen(check: ServiceCheck): FixScreen {
  switch (check) {
    case 'CERTIFICATE_EXPIRING':
    case 'DOMAIN_PROBLEM':
      return 'domains';
    case 'MAIL_FAILED':
    case 'NO_CONTACT':
      return 'contact';
    case 'COPY_FAILED':
      return 'copy';
    case 'ACCOUNT_LOCKED':
    case 'FAILED_SIGN_INS':
    case 'OWNER_WITHOUT_PASSWORD':
    case 'SETUP_FAILED':
      return 'store';
  }
}

export const SEVERITY_TONES = {
  HIGH: 'red',
  MEDIUM: 'amber',
  LOW: 'slate',
} as const satisfies Record<ServiceTicketSeverity, BadgeTone>;

export const STATUS_TONES = {
  OPEN: 'red',
  IN_PROGRESS: 'blue',
  RESOLVED: 'green',
} as const satisfies Record<ServiceTicketStatus, BadgeTone>;

/** How many open tickets of each group a store has, for the card on its page. */
export function openCounts(tickets: readonly Pick<ServiceTicket, 'status' | 'kind' | 'source'>[]) {
  const open = tickets.filter((ticket) => ticket.status !== 'RESOLVED');
  return {
    security: open.filter((t) => t.source === 'SCAN' && t.kind === 'SECURITY').length,
    general: open.filter((t) => t.source === 'SCAN' && t.kind === 'GENERAL').length,
    calls: open.filter((t) => t.source === 'STORE').length,
    total: open.length,
  };
}
